import { useMemo, useState } from 'react';
import { IconAlarm, IconAlertTriangle, IconArrowsExchange, IconCalendarEvent, IconCheck, IconChevronLeft, IconChevronRight, IconClockPause, IconFileInvoice, IconInbox, IconLock, IconPlus, IconX, IconBoxMultiple } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Modal, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { STAGE_LABEL } from './logic';
import { dealAbbr, followLong, followShort, neighbours, type FollowTone } from './pipeline';
import { FOLLOW_OPTS, LOST_REASONS, SEGS, funnel, inSeg, isJobId, openCount, scopeDeals, winRate, type Scope, type Seg } from './pipelineCompactData';
import type { Deal, DealStage } from './types';
import s from './pipelineCompact.module.css';

// Mint-Pipeline-iPad (Kanban 5 cột, nút ‹ › thay kéo thả) · Mint-Pipeline-Phone (đoạn giai đoạn → chi tiết phiếu + bảng trượt Won / Lost / follow).
// Chỉ đọc: mọi nút ghi (chuyển giai đoạn, chốt Won / Lost, đặt follow, tạo phiếu) báo toast "Chưa mở ghi ở bản này". Không hiện tiền.

const DOT: Record<DealStage, string | undefined> = { New: s.dNew, Pricing: s.dPricing, Quoted: s.dQuoted, Won: s.dWon, Lost: s.dLost };
const FU: Record<FollowTone, string | undefined> = { none: s.fuNone, over: s.fuOver, today: s.fuToday, normal: '' };
const FLOW: DealStage[] = ['New', 'Pricing', 'Quoted', 'Won', 'Lost'];

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function FuIcon({ d }: { d: Deal }) {
  if (d.stage === 'Won' || d.stage === 'Lost') return <IconCheck size={14} stroke={1.8} aria-hidden="true" />;
  if (d.followDays === null) return <IconClockPause size={14} stroke={1.8} aria-hidden="true" />;
  return d.followDays < 0 ? <IconAlertTriangle size={14} stroke={1.8} aria-hidden="true" /> : <IconAlarm size={14} stroke={1.8} aria-hidden="true" />;
}
const fuText = (d: Deal) => (d.stage === 'Won' || d.stage === 'Lost' ? d.closeNote : followShort(d).text);
const fuTone = (d: Deal) => (d.stage === 'Won' || d.stage === 'Lost' ? s.fuNone : FU[followShort(d).tone]);
const fuTitle = (d: Deal) => (d.stage === 'Won' || d.stage === 'Lost' ? d.closeNote : followLong(d));

function Funnel({ rows }: { rows: readonly Deal[] }) {
  const t = useT();
  const f = funnel(rows);
  const aria = `Phiếu theo giai đoạn: ${f.map((x) => `${x.label} ${x.n}`).join(', ')}`;
  return (
    <section className={s.fun} aria-label={t('crm.pc.funnel')}>
      <div className={s.funHead}>
        <b>{t('crm.pc.funnel')}</b>
        <span>
          <span className={s.lb}>{t('crm.pc.winRate')}</span> <b className={s.lab}>{winRate(rows)}</b>
        </span>
      </div>
      <div className={s.bar} role="img" aria-label={aria}>
        {f.filter((x) => x.n > 0).map((x) => (
          <div key={x.stage} className={`${s.seg} ${DOT[x.stage]}`} style={{ flex: `${x.n} 1 0` }} />
        ))}
      </div>
      <div className={s.legend}>
        {f.map((x) => (
          <span key={x.stage} className={s.lg}>
            <span className={`${s.dot} ${DOT[x.stage]}`} aria-hidden="true" />
            {x.label} <b>{x.n}</b>
          </span>
        ))}
      </div>
    </section>
  );
}

export function PipelineCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const { data, error, isLoading, isError, refetch } = useList('crmDeal', { limit: 5000 });
  const forced = readState();
  const [scope, setScope] = useState<Scope>('mine');
  const [seg, setSeg] = useState<Seg>('Quoted');
  const [cur, setCur] = useState<string | null>(null);
  const [modal, setModal] = useState<'won' | 'lost' | 'fu' | null>(null);
  const [job, setJob] = useState('');
  const [tried, setTried] = useState(false);
  const [reason, setReason] = useState<string>(LOST_REASONS[0]);
  const [fuPick, setFuPick] = useState<string | null>(null);

  const all = data?.items ?? [];
  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (isLoading) state = 'loading';
  else if (isError) state = error instanceof DataError && error.code === 'noperm' ? 'noperm' : 'error';
  else if (all.length === 0) state = 'empty';
  const noperm = state === 'noperm';
  const ready = state === 'ready' || noperm;
  const rows = useMemo(() => scopeDeals(all, scope, !noperm), [all, scope, noperm]);
  const deal = cur ? all.find((d) => d.id === cur) ?? null : null;

  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const openDeal = (d: Deal) => (phone ? setCur(d.id) : toast({ msg: 'Chi tiết phiếu chưa mở ở bản này' }));

  const scopes = (
    <div className={s.scopes} role="radiogroup" aria-label={t('crm.at.81')}>
      {(noperm ? ([['mine', 'Của tôi']] as const) : ([['mine', 'Của tôi'], ['team', 'Cả nhóm']] as const)).map(([k, label]) => (
        <button key={k} type="button" role="radio" aria-checked={(noperm ? 'mine' : scope) === k} className={s.scope} onClick={() => setScope(k)}>
          {label}
        </button>
      ))}
    </div>
  );

  /* ---- Điện thoại · khung 2: chi tiết phiếu ---- */
  if (phone && deal && ready) {
    const nb = neighbours(deal);
    const di = FLOW.indexOf(deal.stage);
    const closeModal = () => { setModal(null); setTried(false); };
    const okWon = () => (isJobId(job) ? (closeModal(), noWrite()) : setTried(true));
    return (
      <Page>
        <PageHeader
          title={deal.customer}
          sub={deal.id}
          lead={
            <button type="button" className={s.back} aria-label={t('crm.at.82')} onClick={() => setCur(null)}>
              <IconChevronLeft size={22} aria-hidden="true" />
            </button>
          }
        />
        <div className={s.detail}>
          <div className={s.card}>
            <div className={s.stepper} aria-hidden="true">
              {FLOW.map((st, i) => (
                <div key={st} className={`${s.step} ${i === di || (i < di && di < 4) ? DOT[st] : ''}`} />
              ))}
            </div>
            <div className={s.row1}>
              <b>{STAGE_LABEL[deal.stage]}</b>
              <span className={`${s.sale} ${s.lb}`} title={`Sale ${deal.sale}`}>
                <span className={s.ab} aria-hidden="true">{dealAbbr(deal.sale)}</span>
                {deal.sale}
              </span>
            </div>
            <div className={s.subj}>{deal.subject}</div>
            <div className={s.metaRow}>
              <span className={s.meta} title={`Dịch vụ ${deal.services.join(' · ')}`}>
                <IconBoxMultiple size={15} stroke={1.8} aria-hidden="true" />
                {deal.services.join(' · ')}
              </span>
              <span className={`${s.meta} ${fuTone(deal)}`} title={fuTitle(deal)}>
                <FuIcon d={deal} />
                {fuTitle(deal)}
              </span>
            </div>
          </div>
          <div className={s.card}>
            <div className={`${s.lb} ${s.sm}`}>
              <IconFileInvoice size={14} stroke={1.8} aria-hidden="true" /> {t('crm.x.quote')}
            </div>
            <div className={s.lab}>{deal.quotes.length ? deal.quotes.join(', ') : 'Chưa gắn'}</div>
            <div className={`${s.lb} ${s.sm} ${s.gap}`}>Pricing</div>
            <div className={s.lab}>{deal.pricingRequest ? `${deal.pricingRequest} · ${deal.pricingStatus}` : 'Chưa hỏi'}</div>
          </div>
          <button type="button" className={s.big} onClick={() => setModal('fu')}>
            <IconCalendarEvent size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.x.setFollow')}
          </button>
          {nb.next && deal.stage !== 'Lost' && (
            <button type="button" className={`${s.big} ${s.bigPri}`} onClick={() => (nb.next === 'Won' ? setModal('won') : noWrite())}>
              <IconArrowsExchange size={16} stroke={1.8} aria-hidden="true" />
              Chuyển sang {nb.next}
            </button>
          )}
        </div>
        <div className={s.dock}>
          <button type="button" className={s.big} onClick={() => setModal('lost')}>
            <IconX size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.pc.lost')}
          </button>
          <button type="button" className={`${s.big} ${s.bigInk}`} onClick={() => setModal('won')}>
            <IconCheck size={16} stroke={1.8} aria-hidden="true" />
            {t('crm.pc.won')}
          </button>
        </div>
        <Modal open={modal === 'won'} onOpenChange={(o) => !o && closeModal()} title={`${t('crm.pc.won')} · ${deal.id}`} footer={<Button variant="primary" onClick={okWon}>{t('crm.pc.won')}</Button>}>
          <label className={s.fld}>
            <span className={s.lab}>JobID <span className={s.req}>*</span></span>
            <input value={job} onChange={(e) => setJob(e.target.value)} placeholder="SE2606…" aria-invalid={tried && !isJobId(job)} />
          </label>
          <div className={`${s.sm} ${tried && !isJobId(job) ? s.errT : s.lb}`}>{tried && !isJobId(job) ? 'JobID dạng SE260639171.' : 'Job chọn Ref báo giá sẽ tự Won.'}</div>
        </Modal>
        <Modal open={modal === 'lost'} onOpenChange={(o) => !o && closeModal()} title={`${t('crm.pc.lost')} · ${deal.id}`} footer={<Button variant="primary" onClick={() => { closeModal(); noWrite(); }}>{t('crm.pc.lost')}</Button>}>
          <div className={s.chips} role="radiogroup" aria-label={t('crm.at.83')}>
            {LOST_REASONS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={reason === k} className={s.opt} onClick={() => setReason(k)}>{k}</button>
            ))}
          </div>
          <input className={s.in} aria-label={t('crm.at.84')} placeholder={t('crm.at.85')} />
        </Modal>
        <Modal open={modal === 'fu'} onOpenChange={(o) => !o && closeModal()} title={`Ngày follow · ${deal.id}`} footer={<Button variant="primary" onClick={() => { closeModal(); noWrite(); }}>Xong</Button>}>
          <div role="radiogroup" aria-label={t('crm.at.86')} className={s.opts}>
            {FOLLOW_OPTS.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={fuPick === k} className={`${s.opt} ${s.optW}`} onClick={() => setFuPick(k)}>{k}</button>
            ))}
          </div>
        </Modal>
      </Page>
    );
  }

  /* ---- Khung 1: danh sách (điện thoại) / Kanban (iPad) ---- */
  const shown = phone ? rows.filter((d) => inSeg(d, seg)) : rows;
  const body = ready && (
    <>
      {noperm && (
        <div role="note" className={s.note}>
          <IconLock size={16} aria-hidden="true" /> {t('crm.x.ownOnly')}
        </div>
      )}
      {rows.length > 0 && <Funnel rows={rows} />}
      {phone ? (
        <div className={s.list}>
          {shown.map((d) => (
            <button key={d.id} type="button" className={`${s.pc} ${d.id === cur ? s.pcOn : ''}`} onClick={() => openDeal(d)}>
              <span className={s.l1}>
                <b className={s.grow}>{d.customer}</b>
                <span className={`${s.meta} ${s.lb2}`}>
                  <span className={`${s.dot} ${DOT[d.stage]}`} aria-hidden="true" />
                  {STAGE_LABEL[d.stage]}
                </span>
              </span>
              <span className={s.lb2}>{d.subject}</span>
              <span className={s.l1}>
                <span className={`${s.meta} ${fuTone(d)}`} title={fuTitle(d)}>
                  <FuIcon d={d} />
                  {fuText(d)}
                </span>
                <span className={`${s.meta} ${s.lb}`} title={`Sale ${d.sale}`}>
                  <span className={s.ab} aria-hidden="true">{dealAbbr(d.sale)}</span>
                  {d.sale}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className={s.cols}>
          {FLOW.map((st) => {
            const g = rows.filter((d) => d.stage === st);
            return (
              <div key={st} className={s.col}>
                <div className={s.colHead}>
                  <span className={`${s.dot} ${DOT[st]}`} aria-hidden="true" />
                  <b>{STAGE_LABEL[st]}</b>
                  <span className={s.lb}>{g.length}</span>
                </div>
                {g.map((d) => {
                  const nb = neighbours(d);
                  return (
                    <div key={d.id} className={s.kc}>
                      <button type="button" className={s.kcOpen} onClick={() => openDeal(d)}>
                        <b>{d.customer}</b>
                        <span className={s.kcSub}>{d.subject}</span>
                        <span className={`${s.meta} ${s.kcFu} ${fuTone(d)}`} title={fuTitle(d)}>
                          <FuIcon d={d} />
                          {fuText(d)}
                        </span>
                      </button>
                      {(nb.prev || nb.next) && (
                        <div className={s.steps}>
                          {nb.prev && d.stage !== 'Won' && d.stage !== 'Lost' && (
                            <button type="button" className={s.prev} aria-label={t('crm.pc.back', { v: nb.prev })} onClick={noWrite}>‹</button>
                          )}
                          {nb.next && d.stage !== 'Lost' && (
                            <button type="button" className={s.next} aria-label={`Chuyển sang ${nb.next}`} onClick={noWrite}>
                              {nb.next}
                              <IconChevronRight size={14} stroke={2} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <Page kind="table">
      <PageHeader
        title={t('crm.at.87')}
        tabs={!phone ? scopes : undefined}
        lead={
          phone ? (
            <a className={s.back} href="/more" aria-label={t('crm.at.5')}>
              <IconChevronLeft size={22} aria-hidden="true" />
            </a>
          ) : undefined
        }
        actions={
          !phone ? (
            <>
              {ready && <span className={s.sum}>{openCount(rows)} phiếu mở</span>}
              <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>
                {t('crm.x.createInq')}
              </Button>
            </>
          ) : undefined
        }
      />
      <div className={s.page}>
        {phone && ready && (
          <Segmented label={t('crm.at.88')} value={seg} onChange={(k) => setSeg(k as Seg)} items={SEGS.map((x) => ({ key: x.key, label: x.label, n: rows.filter((d) => inSeg(d, x.key)).length }))} />
        )}
        <StateView
          state={ready ? 'ready' : state}
          title={state === 'empty' ? 'Chưa có dữ liệu' : undefined}
          desc={state === 'empty' ? 'Chưa có phiếu hỏi giá nào.' : undefined}
          icon={state === 'empty' ? <IconInbox size={26} stroke={1.8} /> : undefined}
          action={state === 'error' ? <Button onClick={() => void refetch()}>{t('crm.retry')}</Button> : undefined}
          skeleton={<Skeleton rows={6} />}
        >
          {body}
        </StateView>
      </div>
    </Page>
  );
}
